import {useEffect, useMemo, useRef, useState} from 'react'
import Chart from 'react-apexcharts'
import TextHelper from '../../../../utils/text'
import {colors} from '../../../../utils/colors'
import classNames from 'classnames'
import {useNamespaceTranslations} from '../../../../utils/hooks/useNamespacesTranslations'

const CONTAINER_PADDING = 22

const Treemap = ({selectedMetric, chartKey, data, isDarkMode, commonMetricsInfo, isInModal = false}) => {
    const serverMetrics = useNamespaceTranslations('server-metrics')?.server_metrics
    const containerRef = useRef()
    const [containerWidth, setContainerWidth] = useState(containerRef?.current?.offsetWidth - CONTAINER_PADDING)
    const chartOptions =  useMemo(() => {
        return {
            chart: {
                animations: {enabled: false},
                height: 261,
                zoom: {enabled: true},
                toolbar: {
                    show: false
                }
            },
            plotOptions: {
                treemap: {
                    distributed: true,
                    enableShades: false,
                    useFillColorAsStroke: false
                }
            },
            dataLabels: {
                enabled: true,
                style: {
                    fontSize: '12px'
                },
                formatter: function(text, op) {
                    return [TextHelper.textLengthChecker(text, 12), op.value]
                },
                offsetY: -4
            },
            title: {
                align: 'left',
                text: undefined,
                style: {
                    fontSize: '18px'
                }
            },
            tooltip: {
                x: {show: false},
                y: {
                    show: true,
                    title: {
                        formatter: (name) => `${selectedMetric?.tooltip_text || name}:`
                    }
                }
            },
            responsive: [
                {
                    breakpoint: 1200,
                    options: {chart: {width: containerWidth - 8}}
                },
                {
                    breakpoint: 992,
                    options: {chart: {width: containerWidth - 12}}
                }],
            colors: [
                colors.colorBlue,
                colors.colorGreen,
                colors.colorRed,
                colors.colorFlameOrange,
                colors.colorSaphireBlue,
                colors.colorTeal,
                colors.colorMuted,
                colors.colorPrimaryHover,
                colors.colorZapier,
                colors.colorOceanicBlue
            ]
        }
    }, [commonMetricsInfo, isDarkMode, containerWidth, selectedMetric?.value])

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef?.current?.offsetWidth) {
                setContainerWidth(containerRef?.current?.offsetWidth - CONTAINER_PADDING)
            }
        }
        updateWidth()
        window.addEventListener('resize', updateWidth)
        return () => window.removeEventListener('resize', updateWidth)
    }, [])

    const chart = Object.values(serverMetrics).find(metric => metric.value === selectedMetric?.value)?.charts
    const modifiedData = data?.length > 0 && data.map(item => ({x: item.name, y: item.data[0]}))
    const chartTitle = !isInModal ? `${chart[chartKey].label} (${data[0].unit})` : ''

    return (
        <div className={classNames('chart-wrapper', 'flex-chart', {
            'processed-chart-wrapper': selectedMetric?.value === serverMetrics.processes_unified.value
        })} ref={containerRef}>
            <div className="chart-title" title={chartTitle}>
                {chartTitle}
            </div>
            {data?.length > 0 && (<Chart
                options={chartOptions}
                series={[{data: modifiedData}]}
                type='treemap'
                height={!isInModal ? 261 : containerRef?.current?.offsetHeight}
                width={containerWidth}
            />
            )}
        </div>
    )
}

export default Treemap
